import PropTypes from 'prop-types';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useState } from 'react';
// @mui
import { Icon as Iconify } from '@iconify/react';
import { Box, Tooltip, Link, ListItemText } from '@mui/material';
// locales
import { useLocales } from '../../../locales';
// auth
import RoleBasedGuard from '../../../auth/RoleBasedGuard';
//
import PopUpHandle from '../../../commons/PopUpHandle';
import { getModuleBasePath } from '../../../layouts/main/ModuleNavigation';
//
import { StyledItem, StyledIcon } from './styles';


// ----------------------------------------------------------------------

NavItem.propTypes = {
    open: PropTypes.bool,
    active: PropTypes.bool,
    item: PropTypes.object,
    depth: PropTypes.number,
    isExternalLink: PropTypes.bool,
};

export default function NavItem({ item, depth, open, active, isExternalLink, ...other }) { // NOSONAR
    const { pathname } = useLocation();
    const { translate } = useLocales();
    const [number, setnumber] = useState(1);
    const [warningOpen, setWarningOpen] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');

    const { displayName, route, icon, info, children, disabled, caption, roles, iconName } = item;

    const normalizeRoutePath = (path) => {
        if (!path) return '/';
        return path.startsWith('/') ? path : `/${path}`;
    };

    const checkTabLimit = (event) => {
        const storedTabCount = sessionStorage.getItem('displayTabCount');
        const displayTabCount = storedTabCount !== null ? Number(storedTabCount) : 0;
        if (displayTabCount <= 0) {
            return true;
        }

        const normalizedRoute = normalizeRoutePath(route);
        const targetBaseRoute = getModuleBasePath(normalizedRoute) || normalizedRoute;

        let currentOpenTabs = [];
        try {
            currentOpenTabs = JSON.parse(sessionStorage.getItem('mainOpenTabs')) || [];
        } catch (e) {
            currentOpenTabs = [];
        }

        const isAlreadyOpen = currentOpenTabs.some((tab) => {
            const tabNorm = normalizeRoutePath(tab.route);
            const tabBase = getModuleBasePath(tabNorm) || tabNorm;
            return tabBase === targetBaseRoute;
        });

        if (!isAlreadyOpen && currentOpenTabs.length >= displayTabCount) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            setWarningMessage(`Maximum open tabs limit of ${displayTabCount} reached. Please close any unused tabs to open a new tab.`);
            setWarningOpen(true);
            return false;
        }
        return true;
    };


    const subItem = depth !== 1;

    const isSubItemActive =
        subItem &&
        (pathname === route || pathname.startsWith(`${route}/`));


    // Add this new logic:
    const isParentOfActiveChild =
        !subItem &&
        children &&
        children.some(child =>
            pathname === child.route || pathname.startsWith(`${child.route}/`)
        );

    const renderContent = (
        <StyledItem
            depth={depth}
            active={active || isSubItemActive}
            disabled={disabled}
            caption={!!caption}
            {...other}
            onClick={(event) => {
                if (!children && !isExternalLink && !checkTabLimit(event)) {
                    return;
                }
                if (other.onClick) {
                    other.onClick(event);
                }
            }}

            // css for adjusting item links 
            sx={{
                height: 'auto', // Ensure height adjusts to content
                padding: '5px 10px', // Add some padding for better spacing
                paddingLeft: subItem ? '32px' : '8px',
                display: 'flex',
                alignItems: 'center',
                // Add these lines to override MUI ListItemIcon size
                '& .MuiListItemIcon-root': {
                    width: 24,
                    height: 24,
                    minWidth: 24,
                },
                // Or target all icons within
                '& svg': {
                    width: 16,
                    height: 16,
                }
            }}
        >
            {/* Render icon from API or fallback to prop icon */}
            {(iconName || icon) && (
                <StyledIcon
                    sx={{
                        color: (
                            (subItem && (active || isSubItemActive)) ||
                            (!subItem && (active || isParentOfActiveChild))
                        ) ? '#0351AB' : '#1c84c6',
                        marginRight: '5px',
                    }}
                >
                    {iconName ? (
                        <Iconify
                            icon={iconName}
                            width={20}
                            height={20}
                            sx={{ mr: 0 }}
                        />
                    ) : (
                        icon
                    )}
                </StyledIcon>
            )}









            <ListItemText
                primary={`${translate(displayName)}`}
                secondary={
                    caption && (
                        <Tooltip title={`${translate(caption)}`} placement="top-start">
                            <span>{`${translate(caption)}`}</span>
                        </Tooltip>
                    )
                }
                primaryTypographyProps={{
                    // noWrap: true,
                    component: 'span',
                    variant: 'body2',
                    sx: {
                        color: active || isSubItemActive ? '#0351AB' : 'inherit',
                        fontWeight: active || isSubItemActive ? 'bold' : 'normal', // Make bold if active
                        // fontSize: active || isSubItemActive ? '1rem' : '0.875rem', // Increase font size if active

                    },
                }}
                secondaryTypographyProps={{
                    noWrap: true,
                    variant: 'caption',
                }}
            />

            {info && (
                <Box component="span" sx={{ lineHeight: 0 }}>
                    {info}
                </Box>
            )}

            {!!children && (
                <Iconify
                    width={16}
                    icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
                    sx={{ ml: 1, flexShrink: 0 }}
                />
            )}
        </StyledItem>
    );

    const handleClear = async () => {
        if (/^\/?main\/(?:upload-document|posted-filter)(?:\/|$)/.test(route)) return;
        if (/^\/?(?:masters|loyalty|approval|approvalhierarchy|adjustment|adjustments|[Cc]laim|claim-submission|contract|reports|scheme)(?:\/|$)/.test(route)) {
            return;
        }
        setnumber(prev => prev + 1)

        sessionStorage.setItem("number", number);
        sessionStorage.removeItem('filteredUrl');
        sessionStorage.removeItem('filterPayload');
        sessionStorage.removeItem('filterValues');
        sessionStorage.removeItem('gridPageNumber');

        // Scheme Claim Approval change....
        sessionStorage.removeItem('searchClaimData');
        sessionStorage.setItem('ClaimPageNo', 1);

        sessionStorage.removeItem('CAHDetails');
        sessionStorage.removeItem('ClaimSubmissionDetails');
        sessionStorage.removeItem('ClaimClosureHistoryReportFilterDetails');
        sessionStorage.removeItem('SchemeDefinitionReportFilterState');
        sessionStorage.removeItem('loyaltyDetails');
        sessionStorage.removeItem("contractClaimApprovalDetails");
        sessionStorage.removeItem("ClaimSummaryReportFilterState");
        sessionStorage.removeItem("fillPayloadSCA");
    }

    const handleMenuClick = (event) => {
        const storedTabCount = sessionStorage.getItem('displayTabCount');
        const displayTabCount = storedTabCount !== null ? Number(storedTabCount) : 0;

        if (displayTabCount > 0) {
            if (!checkTabLimit(event)) {
                return;
            }

        }
        const normalizedRoute = normalizeRoutePath(route);
        window.dispatchEvent(new CustomEvent('openMainTab', {
            detail: {
                route: normalizedRoute,
                currentPath: normalizedRoute,
                label: translate(displayName),
            },
        }));
        handleClear();
    };

    const renderItem = () => {

        // ExternalLink..

        if (isExternalLink)
            return (
                <Link href={route} target="_blank" rel="noopener" underline="none" onClick={() => handleClear()}>
                    {renderContent}
                </Link>
            );

        // Has child
        if (children) {
            return renderContent;
        }

        // Default
        return (
            <>
                <Link component={RouterLink} to={route} underline="none" onClick={handleMenuClick}>
                    {renderContent}
                </Link>
                {warningOpen && (
                    <PopUpHandle
                        open={warningOpen}
                        handleClose={() => setWarningOpen(false)}
                        errorCode={400}
                        error={warningMessage}
                    />
                )}
            </>
        );
    };

    return <RoleBasedGuard roles={roles}>{renderItem()}</RoleBasedGuard>;
}

